<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-account-plus</v-icon>
      <span class="text-h5">Nouvelle candidature</span>
    </v-card-title>

    <v-card-text>
      <v-form ref="applicationForm" @submit.prevent="handleSubmit">
        <!-- Informations personnelles -->
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.prenom" label="Prénom *" variant="outlined" :rules="[rules.required]"
              prepend-inner-icon="mdi-account" required></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.nom" label="Nom *" variant="outlined" :rules="[rules.required]"
              prepend-inner-icon="mdi-account" required></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.email" label="Email *" type="email" variant="outlined"
              :rules="[rules.required, rules.email]" prepend-inner-icon="mdi-email" required></v-text-field>
          </v-col>
        </v-row>

        <!-- Poste souhaité -->
        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.ficheJobId" label="ID du poste *" variant="outlined" :rules="[rules.required]"
              prepend-inner-icon="mdi-briefcase"
              hint="Saisissez l'identifiant du poste pour lequel vous souhaitez postuler" persistent-hint
              :loading="isLoadingJobTitle" @input="handleJobIdChange" required></v-text-field>
          </v-col>
        </v-row>

        <!-- Affichage du titre du poste -->
        <v-row v-if="jobInfo">
          <v-col cols="12">
            <v-alert :type="jobInfo.isValidate ? 'success' : 'warning'" variant="tonal" class="mb-4">
              <div class="d-flex align-center">

                <div>
                  <div class="font-weight-bold">{{ jobInfo.title }}</div>
                  <div class="text-caption">
                    {{ jobInfo.isValidate ? 'Poste validé et ouvert aux candidatures' : 'Poste en cours de validation'
                    }}
                  </div>
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Upload CV -->
        <v-row>
          <v-col cols="12">
            <v-file-input v-model="form.cvPdf" label="CV (PDF) *" variant="outlined" accept=".pdf"
              :rules="[rules.fileRequired, rules.pdfFile]" prepend-inner-icon="mdi-file-pdf-box" show-size required>
              <template v-slot:selection="{ fileNames }">
                <template v-for="fileName in fileNames" :key="fileName">
                  <v-chip color="primary" size="small" class="me-2">
                    <v-icon start icon="mdi-file-pdf-box"></v-icon>
                    {{ fileName }}
                  </v-chip>
                </template>
              </template>
            </v-file-input>
          </v-col>
        </v-row>

        <!-- Message d'information -->
        <v-row>
          <v-col cols="12">
            <v-alert type="info" variant="tonal">
              <v-icon class="mr-2">mdi-information</v-icon>
              Votre candidature sera traitée dans les plus brefs délais.
              Vous recevrez un email de confirmation avec un numéro de suivi.
            </v-alert>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn @click="$emit('close')">
        Annuler
      </v-btn>
      <v-btn color="primary" variant="elevated" :loading="isSubmitting" :disabled="!isFormValid" @click="handleSubmit">
        <v-icon start>mdi-send</v-icon>
        Envoyer ma candidature
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { apiService } from '@/services/api'
import { useSnackbarStore } from '@/stores/snackbar'
import { computed, ref, watch } from 'vue'

interface JobInfo {
  id: string
  jobId: string
  title: string
  isValidate: boolean
}

interface CandidateApplication {
  nom: string
  prenom: string
  email: string
  ficheJobId: string
  fichePosteTitle: string,
  fichePosteId: string,
  cvPdf: File
}

const emit = defineEmits<{
  close: []
  submitted: [application: any]
}>()

const snackbarStore = useSnackbarStore()

const applicationForm = ref()
const isSubmitting = ref(false)
const isLoadingJobTitle = ref(false)
const jobInfo = ref<JobInfo | null>(null)

const form = ref<CandidateApplication>({
  nom: '',
  prenom: '',
  email: '',
  ficheJobId: '',
  fichePosteTitle: '',
  fichePosteId: '',
  cvPdf: null
})

const rules = {
  required: (value: string) => !!value || 'Ce champ est requis',
  fileRequired: (value: File[] | null) => {
    console.log('Validation fileRequired - valeur reçue:', value);
    if (!value || value.length === 0) {
      console.log('Aucun fichier sélectionné');
      return 'Veuillez sélectionner un fichier';
    }
    return true;
  },
  email: (value: string) => /.+@.+\..+/.test(value) || 'Email invalide',
  pdfFile: (file: File | null) => {
    console.log('Validation pdfFile - valeur reçue:', file);
    if (!file) {
      console.log('Le fichier est undefined ou null');
      return 'Veuillez sélectionner un fichier valide';
    }
    if (typeof file.type !== 'string') {
      console.log('Type de fichier non valide ou manquant:', file.type);
      return 'Veuillez sélectionner un fichier valide';
    }
    if (file.type !== 'application/pdf') {
      console.log('Type de fichier invalide:', file.type);
      return 'Le fichier doit être au format PDF';
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB max
      console.log('Taille de fichier trop grande:', file.size);
      return 'Le fichier ne doit pas dépasser 5MB';
    }
    console.log('Validation pdfFile réussie pour le fichier:', file.name);
    return true;
  },
};

const isFormValid = computed(() => {
  console.log('Validation isFormValid triggered');
  const isNomValid = form.value.nom?.trim() !== '';
  const isPrenomValid = form.value.prenom?.trim() !== '';
  const isEmailValid = form.value.email?.trim() !== '' && /.+@.+\..+/.test(form.value.email);
  const isficheJobIdValid = form.value.ficheJobId?.trim() !== '';
  const isFichePosteTitleValid = form.value.fichePosteTitle?.trim() !== '';
  const isCvPdfValid = form.value.cvPdf && form.value.cvPdf.type === 'application/pdf';

  console.log('Validation results:', {
    isNomValid,
    isPrenomValid,
    isEmailValid,
    isficheJobIdValid,
    isFichePosteTitleValid,
    isCvPdfValid,
  });

  return isNomValid && isPrenomValid && isEmailValid && isficheJobIdValid && isFichePosteTitleValid && isCvPdfValid;
})

// Debounce pour éviter trop d'appels API
let timeoutId: number | null = null

const handleJobIdChange = () => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }

  timeoutId = setTimeout(() => {
    if (form.value.ficheJobId.trim()) {
      loadJobTitle()
    } else {
      jobInfo.value = null
      form.value.fichePosteTitle = ''
    }
  }, 500) as unknown as number
}

const loadJobTitle = async () => {
  if (!form.value.ficheJobId.trim()) return

  try {
    isLoadingJobTitle.value = true

    // Appel API pour récupérer les informations du poste
    const response = await apiService.candidate.getJobById(form.value.ficheJobId)

    if (response.data) {
      jobInfo.value = response.data
      form.value.fichePosteTitle = jobInfo.value.title
      form.value.fichePosteId = jobInfo.value.id
      if (!response.data.isValidate) {
        snackbarStore.warning('Ce poste est en cours de validation et pourrait ne pas être disponible')
      }
    } else {
      jobInfo.value = null
      form.value.fichePosteTitle = ''
      form.value.fichePosteId = ''
      snackbarStore.error('Poste introuvable avec cet ID')
    }
  } catch (error: any) {
    console.error('Erreur lors du chargement du poste:', error)
    jobInfo.value = null
    form.value.fichePosteTitle = ''
    form.value.fichePosteId = ''
    snackbarStore.error('Erreur lors de la récupération des informations du poste')
  } finally {
    isLoadingJobTitle.value = false
  }
}

watch(isFormValid, (newValue) => {
  console.log('isFormValid:', newValue);
});

const handleSubmit = async () => {
  console.log('handleSubmit triggered');
  const { valid } = await applicationForm.value.validate();
  console.log('Form validation result:', valid);
  if (!valid) return

  if (!jobInfo.value) {
    snackbarStore.error('Veuillez sélectionner un poste valide');
    return
  }

  try {
    isSubmitting.value = true;
    console.log('Submitting form data:', form.value);

    // Créer FormData pour l'upload de fichier
    const formData = new FormData()
    formData.append('nom', form.value.nom)
    formData.append('prenom', form.value.prenom)
    formData.append('email', form.value.email)
    formData.append('ficheJobId', form.value.ficheJobId)
    formData.append('fichePosteTitle', form.value.fichePosteTitle)
    formData.append('fichePosteId', form.value.fichePosteId)
    formData.append('cvPdf', form.value.cvPdf)


    // Appel API pour soumettre la candidature
    const response = await apiService.candidate.submitApplication(formData);

    if (response.data) {
      snackbarStore.success('Candidature envoyée avec succès !')

      // Émettre l'événement avec les données de la candidature
      emit('submitted', {
        id: response.data?.id || Date.now(),
        jobTitle: form.value.fichePosteTitle,
        status: 'applied',
        appliedAt: new Date().toISOString(),
        notes: 'Candidature soumise avec succès'
      })

      // Réinitialiser le formulaire
      form.value = {
        nom: '',
        prenom: '',
        email: '',
        ficheJobId: '',
        fichePosteTitle: '',
        cvPdf: null
      }
      jobInfo.value = null

    } else {
      snackbarStore.error('Erreur lors de l\'envoi de la candidature')
    }
  } catch (error: any) {
    console.error('Erreur lors de la soumission:', error)
    snackbarStore.error('Erreur lors de l\'envoi de la candidature')
  } finally {
    isSubmitting.value = false
  }
}

// Surveiller les changements du ficheJobId
watch(() => form.value.ficheJobId, handleJobIdChange)
</script>

<style scoped>
.v-file-input {
  margin-bottom: 1rem;
}

.v-alert {
  border-radius: 8px;
}

.v-chip {
  margin: 2px;
}
</style>
